import { useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Camera,
  Search,
  X,
  Check,
  Loader2,
  ChevronRight,
  ChevronLeft,
  Gauge,
  FolderOpen,
  AlertTriangle,
  CheckCircle2,
  ArrowLeft,
  LogOut,
  Image,
} from 'lucide-react';
import { Button, Card, Select, Input, Spinner } from '@/components/common';
import { projects as projectsApi, zaehler as zaehlerApi } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

// Tab component for mobile
const TabButton = ({ active, icon: Icon, label, onClick }) => (
  <button
    onClick={onClick}
    className={`flex-1 flex items-center justify-center gap-2 py-4 font-medium transition-all border-b-2 ${
      active
        ? 'border-primary-500 text-primary-600 bg-primary-50'
        : 'border-transparent text-gray-500 hover:text-gray-700'
    }`}
  >
    <Icon className="w-5 h-5" />
    <span>{label}</span>
  </button>
);

const BauleiterKIZaehler = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [activeTab, setActiveTab] = useState('erfassen');

  return (
    <div className="min-h-screen bg-gray-50 mobile-layout">
      {/* Mobile Header */}
      <header className="bg-primary-500 text-white sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={() => navigate('/bauleiter')}
            className="p-2 hover:bg-primary-600 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Gauge className="w-5 h-5" />
            <h1 className="text-lg font-semibold">Zähler-Erfassung</h1>
          </div>
          <button
            onClick={logout}
            className="p-2 hover:bg-primary-600 rounded-lg transition-colors"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Foto-Erkennung seit 21.10.2026 auf Eis (keine Claude-API, noch kein lokales Bildmodell) */}
      <div className="mx-4 mt-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        Die automatische Zähler-Erkennung aus Fotos ist vorübergehend deaktiviert. Bereits erfasste Zähler lassen sich weiter prüfen und bearbeiten.
      </div>

      {/* Tabs */}
      <div className="flex bg-white border-b border-gray-200 sticky top-[52px] z-30">
        <TabButton
          active={activeTab === 'erfassen'}
          icon={Camera}
          label="Erfassen"
          onClick={() => setActiveTab('erfassen')}
        />
        <TabButton
          active={activeTab === 'pruefen'}
          icon={Search}
          label="Prüfen"
          onClick={() => setActiveTab('pruefen')}
        />
      </div>

      {/* Content */}
      <div className="p-4 pb-24">
        {activeTab === 'erfassen' ? (
          <ErfassenTab />
        ) : (
          <PruefenTab />
        )}
      </div>

      {/* Mobile Styling */}
      <style>{`
        .mobile-layout button,
        .mobile-layout .clickable {
          min-height: 48px;
        }
        .mobile-layout input,
        .mobile-layout select {
          font-size: 16px;
          min-height: 48px;
        }
      `}</style>
    </div>
  );
};

// ============================================
// ERFASSEN TAB
// ============================================
const ErfassenTab = () => {
  const [step, setStep] = useState(1);
  const [selectedProject, setSelectedProject] = useState(null);
  const [bereich, setBereich] = useState('');
  const [photos, setPhotos] = useState([]);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const photoBase64MapRef = useRef(new Map());
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [results, setResults] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Google Drive status
  const [uploadingToDrive, setUploadingToDrive] = useState(false);

  const { data: projects = [], isLoading: loadingProjects } = useQuery({
    queryKey: ['projects-active'],
    queryFn: () => projectsApi.getAll({ status: 'active' }).then(r => r.data || []),
  });

  const { data: driveStatus } = useQuery({
    queryKey: ['zaehler-drive-status'],
    queryFn: () => zaehlerApi.getDriveStatus().then(r => r.data).catch((err) => {
      console.warn('Drive status check failed:', err);
      return { configured: false };
    }),
  });

  const driveConfigured = driveStatus?.configured ?? false;

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Handle file selection - no limit
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Warning for large batches
    if (photos.length + files.length > 50) {
      toast.error('Hinweis: Viele Fotos - Verarbeitung kann länger dauern');
    }

    const timestamp = Date.now();
    const newPhotos = [];

    // Process each file
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const photoData = {
        id: Math.random().toString(36).substr(2, 9),
        file,
        preview: URL.createObjectURL(file),
        driveFileId: null,
        uploading: driveConfigured,
      };
      newPhotos.push(photoData);
    }

    // Add photos immediately (shows upload status)
    setPhotos((prev) => [...prev, ...newPhotos]);

    // Upload to Drive in background if configured
    if (driveConfigured) {
      setUploadingToDrive(true);
      const startIndex = photos.length;

      for (let i = 0; i < newPhotos.length; i++) {
        try {
          const base64 = await fileToBase64(newPhotos[i].file);
          const response = await zaehlerApi.uploadTemp(base64, startIndex + i, timestamp);

          if (response.data.success && response.data.driveConfigured) {
            // Update photo with Drive file ID
            setPhotos((prev) =>
              prev.map((p) =>
                p.id === newPhotos[i].id
                  ? { ...p, driveFileId: response.data.fileId, uploading: false }
                  : p
              )
            );
          } else {
            // Mark as not uploading (failed or not configured)
            setPhotos((prev) =>
              prev.map((p) =>
                p.id === newPhotos[i].id ? { ...p, uploading: false } : p
              )
            );
          }
        } catch (err) {
          console.warn('Drive upload failed for photo:', err);
          setPhotos((prev) =>
            prev.map((p) =>
              p.id === newPhotos[i].id ? { ...p, uploading: false } : p
            )
          );
        }
      }
      setUploadingToDrive(false);
    }

    // Reset inputs
    if (cameraInputRef.current) cameraInputRef.current.value = '';
    if (galleryInputRef.current) galleryInputRef.current.value = '';
  };

  // Remove photo (and delete from Drive if uploaded)
  const removePhoto = async (photoId) => {
    const photo = photos.find((p) => p.id === photoId);

    // Delete from Drive if it was uploaded
    if (photo?.driveFileId) {
      try {
        await zaehlerApi.deleteTemp(photo.driveFileId);
      } catch (err) {
        console.warn('Failed to delete temp file from Drive:', err);
      }
    }

    setPhotos(photos.filter((p) => p.id !== photoId));
  };

  const processPhotos = async () => {
    setStep(3);
    setResults([]);
    setCurrentPhotoIndex(0);
    photoBase64MapRef.current.clear();

    const newResults = [];

    for (let i = 0; i < photos.length; i++) {
      setCurrentPhotoIndex(i);
      const photo = photos[i];

      try {
        const base64 = await fileToBase64(photo.file);
        photoBase64MapRef.current.set(photo.id, base64);
        const response = await zaehlerApi.analyze(base64);

        newResults.push({
          ...response.data,
          photoId: photo.id,
          preview: photo.preview,
          driveFileId: photo.driveFileId, // Keep Drive reference
        });
      } catch (err) {
        newResults.push({
          success: false,
          error: err.message || 'Verbindungsfehler',
          photoId: photo.id,
          preview: photo.preview,
          driveFileId: photo.driveFileId,
        });
      }

      setResults([...newResults]);
    }

    setStep(4);
  };

  const saveResults = async () => {
    setSaving(true);
    const successfulResults = results.filter((r) => r.success);

    try {
      const response = await zaehlerApi.save({
        zaehler: successfulResults.map((r) => ({
          seriennummer: r.seriennummer || null,
          kennnummer: r.kennnummer || null,
          wohnungsnummer: r.wohnungsnummer || null,
          zaehlerstand: r.zaehlerstand || null,
          einheit: r.einheit || 'kWh',
          foto_base64: photoBase64MapRef.current.get(r.photoId) || null,
          driveFileId: r.driveFileId || null,
        })),
        project_id: selectedProject.id,
        project_name: selectedProject.name,
        bereich: bereich,
      });

      // Show success message with Drive info if available
      const driveCount = response.data.driveResults?.length || 0;
      if (driveCount > 0) {
        toast.success(`${successfulResults.length} Zähler gespeichert! Fotos in Drive.`);
      } else {
        toast.success(`${successfulResults.length} Zähler gespeichert!`);
      }
      setSaved(true);
    } catch (err) {
      toast.error('Fehler: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setSelectedProject(null);
    setBereich('');
    setPhotos([]);
    setResults([]);
    setSaved(false);
    photoBase64MapRef.current.clear();
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <Card className="p-4">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Gauge className="w-5 h-5 text-primary-500" />
              Zähler erfassen
            </h2>

            {loadingProjects ? (
              <div className="flex justify-center py-8">
                <Spinner size="lg" />
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bauvorhaben *
                  </label>
                  <Select
                    value={selectedProject?.id || ''}
                    onChange={(e) => {
                      const project = projects.find((p) => p.id === e.target.value);
                      setSelectedProject(project || null);
                    }}
                    options={projects.map((p) => ({ value: p.id, label: p.name }))}
                    placeholder="Bauvorhaben wählen..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bereich *
                  </label>
                  <Input
                    value={bereich}
                    onChange={(e) => setBereich(e.target.value)}
                    placeholder="z.B. Haus 29, Etage 0"
                  />
                </div>

                <Button
                  variant="primary"
                  className="w-full py-4 text-lg"
                  onClick={() => setStep(2)}
                  disabled={!selectedProject || !bereich.trim()}
                >
                  Weiter <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            )}
          </Card>
        );

      case 2:
        return (
          <Card className="p-4">
            <div className="mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Camera className="w-5 h-5 text-primary-500" />
                Fotos hochladen
              </h2>
              <p className="text-sm text-gray-500">{selectedProject?.name} - {bereich}</p>
            </div>

            {/* Hidden file inputs */}
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              multiple
              capture="environment"
              onChange={handleFileSelect}
              className="hidden"
            />
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />

            {/* Two buttons for Camera and Gallery */}
            <div className="flex flex-col gap-3 mb-4">
              <button
                onClick={() => cameraInputRef.current?.click()}
                className="flex items-center justify-center gap-3 p-4 bg-primary-500 text-white rounded-xl font-medium text-lg hover:bg-primary-600 transition-colors min-h-[56px]"
              >
                <Camera className="w-6 h-6" />
                Kamera öffnen
              </button>
              <button
                onClick={() => galleryInputRef.current?.click()}
                className="flex items-center justify-center gap-3 p-4 bg-gray-100 text-gray-700 rounded-xl font-medium text-lg border-2 border-gray-200 hover:bg-gray-200 transition-colors min-h-[56px]"
              >
                <Image className="w-6 h-6" />
                Aus Galerie wählen
              </button>
            </div>

            {/* Photo count */}
            {photos.length > 0 && (
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-gray-700">
                  {photos.length} Foto{photos.length !== 1 ? 's' : ''} hochgeladen
                  {uploadingToDrive && (
                    <span className="text-primary-500 ml-2">
                      <Loader2 className="w-4 h-4 inline animate-spin" /> Hochladen...
                    </span>
                  )}
                </p>
                <button
                  onClick={() => setPhotos([])}
                  className="text-sm text-red-500 hover:text-red-700"
                >
                  Alle entfernen
                </button>
              </div>
            )}

            {/* Photo Grid */}
            {photos.length > 0 && (
              <div className="grid grid-cols-2 gap-2">
                {photos.map((photo, index) => (
                  <div key={photo.id} className="relative">
                    <img
                      src={photo.preview}
                      alt={`Foto ${index + 1}`}
                      className={`w-full h-24 object-cover rounded-lg ${photo.uploading ? 'opacity-60' : ''}`}
                    />
                    {/* Upload spinner overlay */}
                    {photo.uploading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-lg">
                        <Loader2 className="w-6 h-6 text-white animate-spin" />
                      </div>
                    )}
                    {/* Drive uploaded indicator */}
                    {photo.driveFileId && !photo.uploading && (
                      <div className="absolute top-1 left-1 w-5 h-5 bg-green-500 text-white rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3" />
                      </div>
                    )}
                    <button
                      onClick={() => removePhoto(photo.id)}
                      className="absolute top-1 right-1 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center shadow-lg"
                    >
                      <X className="w-5 h-5" />
                    </button>
                    <span className="absolute bottom-1 left-1 bg-black/50 text-white text-xs px-2 py-0.5 rounded">
                      {index + 1}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Actions - Fixed at bottom */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 flex gap-3 z-50">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1 py-4">
                <ChevronLeft className="w-5 h-5 mr-1" /> Zurück
              </Button>
              <Button
                variant="primary"
                onClick={processPhotos}
                disabled={photos.length === 0}
                className="flex-1 py-4"
              >
                Verarbeiten ({photos.length})
              </Button>
            </div>
          </Card>
        );

      case 3:
        return (
          <Card className="p-4">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Loader2 className="w-5 h-5 text-primary-500 animate-spin" />
              Verarbeite...
            </h2>

            <div className="mb-4">
              <div className="flex justify-between text-sm mb-2">
                <span>Fortschritt</span>
                <span>{currentPhotoIndex + 1} / {photos.length}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-primary-500 h-3 rounded-full transition-all"
                  style={{ width: `${((currentPhotoIndex + 1) / photos.length) * 100}%` }}
                />
              </div>
            </div>

            <div className="space-y-2">
              {photos.map((photo, index) => {
                const result = results[index];
                const status = index < currentPhotoIndex ? 'done' : index === currentPhotoIndex ? 'processing' : 'pending';

                return (
                  <div
                    key={photo.id}
                    className={`flex items-center gap-3 p-3 rounded-lg ${
                      status === 'done' ? (result?.success ? 'bg-green-50' : 'bg-red-50') :
                      status === 'processing' ? 'bg-primary-50' : 'bg-gray-50'
                    }`}
                  >
                    {status === 'done' ? (
                      result?.success ? <CheckCircle2 className="w-5 h-5 text-green-500" /> : <AlertTriangle className="w-5 h-5 text-red-500" />
                    ) : status === 'processing' ? (
                      <Loader2 className="w-5 h-5 text-primary-500 animate-spin" />
                    ) : (
                      <div className="w-5 h-5 border-2 border-gray-300 rounded-full" />
                    )}
                    <span className="flex-1">
                      Foto {index + 1}
                      {result?.success && <span className="text-sm text-gray-500 ml-2">| {result.kennnummer}</span>}
                    </span>
                  </div>
                );
              })}
            </div>
          </Card>
        );

      case 4:
        const successCount = results.filter((r) => r.success).length;
        const failCount = results.filter((r) => !r.success).length;

        if (saved) {
          return (
            <Card className="p-6 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-xl font-bold mb-2">Gespeichert!</h2>
              <p className="text-gray-600 mb-6">{successCount} Zähler erfasst</p>
              <Button variant="primary" onClick={resetForm} className="w-full py-4">
                <Camera className="w-5 h-5 mr-2" /> Weitere erfassen
              </Button>
            </Card>
          );
        }

        return (
          <div>
            <Card className="p-4 mb-4">
              <h2 className="text-lg font-semibold mb-3">Ergebnis</h2>
              <div className="flex gap-4 mb-4">
                <div className="flex items-center gap-2 text-green-600">
                  <Check className="w-5 h-5" />
                  <span>{successCount} erkannt</span>
                </div>
                {failCount > 0 && (
                  <div className="flex items-center gap-2 text-red-600">
                    <AlertTriangle className="w-5 h-5" />
                    <span>{failCount} fehlgeschlagen</span>
                  </div>
                )}
              </div>

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left">Kenn.</th>
                      <th className="px-3 py-2 text-left">Whg.</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {results.map((result, index) => (
                      <tr key={index} className={result.success ? '' : 'bg-red-50'}>
                        <td className="px-3 py-2">{result.success ? result.kennnummer : '❌'}</td>
                        <td className="px-3 py-2">{result.success ? result.wohnungsnummer : result.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Fixed Actions */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 flex gap-3 z-50">
              <Button variant="outline" onClick={resetForm} className="flex-1 py-4">
                Verwerfen
              </Button>
              <Button
                variant="primary"
                onClick={saveResults}
                loading={saving}
                disabled={successCount === 0}
                className="flex-1 py-4"
              >
                Speichern ({successCount})
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return <div className="pb-24">{renderStep()}</div>;
};

// ============================================
// PRÜFEN TAB
// ============================================
const PruefenTab = () => {
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [expandedBereich, setExpandedBereich] = useState(null);

  const { data: projects = [] } = useQuery({
    queryKey: ['projects-active'],
    queryFn: () => projectsApi.getAll({ status: 'active' }).then(r => r.data || []),
  });

  const { data: zaehlerResult, isLoading: loading } = useQuery({
    queryKey: ['zaehler', selectedProjectId],
    queryFn: () => zaehlerApi.getAll(selectedProjectId ? { project_id: selectedProjectId } : {}).then(r => r.data),
  });

  const zaehlerData = zaehlerResult?.grouped ?? {};

  const findGaps = (zaehlerList) => {
    const kennnummern = zaehlerList.map((z) => parseInt(z.kennnummer)).filter((k) => !isNaN(k)).sort((a, b) => a - b);
    if (kennnummern.length < 2) return [];

    const gaps = [];
    for (let i = 1; i < kennnummern.length; i++) {
      for (let missing = kennnummern[i - 1] + 1; missing < kennnummern[i]; missing++) {
        gaps.push(missing.toString());
      }
    }
    return gaps;
  };

  return (
    <div>
      <Card className="p-4">
        <h2 className="text-lg font-semibold mb-4">Zähler prüfen</h2>

        <Select
          value={selectedProjectId}
          onChange={(e) => setSelectedProjectId(e.target.value)}
          options={[
            { value: '', label: 'Alle Bauvorhaben' },
            ...projects.map((p) => ({ value: p.id, label: p.name })),
          ]}
          className="mb-4"
        />

        {loading ? (
          <div className="flex justify-center py-8">
            <Spinner size="lg" />
          </div>
        ) : Object.keys(zaehlerData).length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Gauge className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Keine Zähler erfasst</p>
          </div>
        ) : (
          <div className="space-y-3">
            {Object.entries(zaehlerData).map(([projectName, bereiche]) => (
              <div key={projectName} className="border rounded-lg overflow-hidden">
                <div className="bg-gray-100 px-3 py-2 font-medium text-sm flex items-center gap-2">
                  <FolderOpen className="w-4 h-4 text-primary-500" />
                  {projectName}
                </div>
                <div className="divide-y">
                  {Object.entries(bereiche).map(([bereich, zaehlerList]) => {
                    const gaps = findGaps(zaehlerList);
                    const isExpanded = expandedBereich === `${projectName}-${bereich}`;

                    return (
                      <div key={bereich}>
                        <button
                          className="w-full px-3 py-3 flex items-center justify-between"
                          onClick={() => setExpandedBereich(isExpanded ? null : `${projectName}-${bereich}`)}
                        >
                          <span className="text-sm">
                            {bereich} ({zaehlerList.length})
                          </span>
                          <span className="flex items-center gap-2">
                            {gaps.length > 0 ? (
                              <AlertTriangle className="w-5 h-5 text-orange-500" />
                            ) : (
                              <CheckCircle2 className="w-5 h-5 text-green-500" />
                            )}
                            <ChevronRight className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                          </span>
                        </button>

                        {isExpanded && (
                          <div className="px-3 pb-3">
                            {gaps.length > 0 && (
                              <div className="bg-orange-50 text-orange-700 text-sm p-2 rounded mb-2 flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4" />
                                Lücke: {gaps.join(', ')}
                              </div>
                            )}
                            <table className="w-full text-xs">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-2 py-1 text-left">Kenn.</th>
                                  <th className="px-2 py-1 text-left">Whg.</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y">
                                {zaehlerList.sort((a, b) => parseInt(a.kennnummer) - parseInt(b.kennnummer)).map((z) => (
                                  <tr key={z.id}>
                                    <td className="px-2 py-1">{z.kennnummer}</td>
                                    <td className="px-2 py-1">{z.wohnungsnummer}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default BauleiterKIZaehler;
